/**
 * ProposalsBoardView — kanban-style workflow view for POS proposals.
 *
 * V1 scope: visual only. Cards auto-position based on bucketProposal() rules.
 * Click a card → view-proposal flow (same as the list view's 👁 action).
 *
 * No drag-and-drop, no quick actions, no touch log yet. Those land in v2 once
 * Barry has reviewed the layout.
 */

import {
  WORKFLOW_COLUMNS,
  groupProposalsByColumn,
  STALLED_THRESHOLD_DAYS,
  computeProposalTemperature,
  proposalAgeDays,
  PROPOSAL_TEMPERATURE_CONFIG,
} from './ProposalsBoardConfig';
import { POS_GOLD } from '../pos-properties/constants';
import { fnErrorMessage } from '../../../../lib/functionError';

import type { ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

type Row = Record<string, any>;  // eslint-disable-line @typescript-eslint/no-explicit-any

interface ProposalsBoardViewProps {
  proposals: Row[];
  loading: boolean;
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string) => string;
  onView: (row: Row) => void;
  /**
   * Action icons rendered on each card. Same render-prop signature as
   * DataTable's `actions`, so the page passes the identical function and
   * conditional logic (admin-only Approve/Reject, ambassador-only Submit,
   * locked-state edit, etc.) just works without duplication.
   */
  actions?: (row: Row) => ReactNode;
  /**
   * Map<proposal_id, latest ambassador-action event>. Powers the one-line
   * activity preview on each card. Empty map = no preview (older proposal,
   * never had an ambassador action, or board mode just opened).
   */
  latestEvents?: Map<string, Row>;
  /** Supabase client used by the Pinned chip's clear action. */
  supabase?: SupabaseClient;
  /** Refresh callback fired after the chip clears a manual_stage. */
  onProposalUpdated?: () => void;
  /** Per-column sort selection (column id → sort key). Defaults to date_desc per column. */
  columnSorts?: Record<string, string>;
  /** Fired when the operator changes a column's sort dropdown. */
  onColumnSortChange?: (colId: string, sortType: string) => void;
}

type ProposalSortKey =
  | 'date_desc' | 'date_asc'
  | 'amount_desc' | 'amount_asc'
  | 'property_asc' | 'property_desc'
  | 'reference_desc' | 'reference_asc';

function sortProposals(rows: Row[], sortKey: ProposalSortKey): Row[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (sortKey) {
      case 'date_asc':
        return (new Date(a.created_at as string).getTime() || 0) - (new Date(b.created_at as string).getTime() || 0);
      case 'amount_desc':
        return (Number(b.annual_total) || 0) - (Number(a.annual_total) || 0);
      case 'amount_asc':
        return (Number(a.annual_total) || 0) - (Number(b.annual_total) || 0);
      case 'property_asc':
        return ((a.property_name as string) || '').localeCompare((b.property_name as string) || '');
      case 'property_desc':
        return ((b.property_name as string) || '').localeCompare((a.property_name as string) || '');
      case 'reference_desc':
        return ((b.reference_code as string) || '').localeCompare((a.reference_code as string) || '');
      case 'reference_asc':
        return ((a.reference_code as string) || '').localeCompare((b.reference_code as string) || '');
      case 'date_desc':
      default:
        return (new Date(b.created_at as string).getTime() || 0) - (new Date(a.created_at as string).getTime() || 0);
    }
  });
  return copy;
}

export default function ProposalsBoardView({
  proposals,
  loading,
  formatCurrency,
  formatDate,
  onView,
  actions,
  latestEvents,
  supabase,
  onProposalUpdated,
  columnSorts,
  onColumnSortChange,
}: ProposalsBoardViewProps) {
  if (loading) {
    return <div className="placeholder-page"><div className="spinner" /></div>;
  }

  const grouped = groupProposalsByColumn(proposals);

  return (
    <>
      <div
        style={{
          margin: '0.75rem 0 1rem',
          padding: '0.625rem 0.875rem',
          background: 'var(--color-bg)',
          border: '1px solid #e2e8f0',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-light)',
        }}
      >
        <strong>Workflow view (preview):</strong> stages auto-derive from proposal activity (status + last view).
        Direct stage moves, quick actions (WhatsApp / call / snooze) and follow-up reminders land in v2.
        Stalled threshold: {STALLED_THRESHOLD_DAYS} days without a customer view.
      </div>

      <div className="ops-board">
        {WORKFLOW_COLUMNS.map((col) => {
          const rawItems = grouped[col.id] || [];
          const sortKey = (columnSorts?.[col.id] || 'date_desc') as ProposalSortKey;
          const items = sortProposals(rawItems, sortKey);
          return (
            <div key={col.id} className="ops-board-column">
              <div className="ops-board-column-header" style={{ borderTopColor: col.color }}>
                <div className="ops-board-column-title-group">
                  <span className="ops-board-column-title">{col.label}</span>
                  <span className="ops-board-column-count">{items.length}</span>
                </div>
                {onColumnSortChange && (
                  <select
                    className="ops-board-column-sort"
                    value={sortKey}
                    onChange={(e) => onColumnSortChange(col.id, e.target.value)}
                  >
                    <optgroup label="Sort">
                      <option value="date_desc">Newest</option>
                      <option value="date_asc">Oldest</option>
                      <option value="amount_desc">Amount &darr;</option>
                      <option value="amount_asc">Amount &uarr;</option>
                      <option value="property_asc">Property A-Z</option>
                      <option value="property_desc">Property Z-A</option>
                      <option value="reference_desc">Reference &darr;</option>
                      <option value="reference_asc">Reference &uarr;</option>
                    </optgroup>
                  </select>
                )}
              </div>
              <div className="ops-board-column-body">
                {items.length === 0 ? (
                  <div className="ops-board-empty" title={col.helpText}>{col.helpText}</div>
                ) : (
                  items.slice(0, 30).map((p) => (
                    <ProposalCard
                      key={p.id as string}
                      proposal={p}
                      columnId={col.id}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      onView={onView}
                      actions={actions}
                      latestEvent={latestEvents?.get(p.id as string)}
                      supabase={supabase}
                      onProposalUpdated={onProposalUpdated}
                    />
                  ))
                )}
                {items.length > 30 && (
                  <div className="ops-board-more">+{items.length - 30} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

interface ProposalCardProps {
  proposal: Row;
  columnId: string;
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string) => string;
  onView: (row: Row) => void;
  actions?: (row: Row) => ReactNode;
  latestEvent?: Row;
  supabase?: SupabaseClient;
  onProposalUpdated?: () => void;
}

function ProposalCard({ proposal, columnId, formatCurrency, formatDate, onView, actions, latestEvent, supabase, onProposalUpdated }: ProposalCardProps) {
  const annualTotal = Number(proposal.annual_total) || 0;
  const ambassadorName = (proposal.ambassador_name as string) || '';
  const ambassadorInitials = toInitials(ambassadorName);
  const propertyName = (proposal.property_name as string) || 'Unnamed property';
  const refCode = (proposal.reference_code as string) || '';
  const isReworkNeeded = proposal.status === 'rejected' && columnId === 'drafting';
  const manualStage = (proposal.manual_stage as string) || null;

  async function handleClearPin(e: React.MouseEvent) {
    e.stopPropagation();
    if (!supabase) return;
    try {
      const { error } = await supabase.functions.invoke('pos-proposal-manage', {
        body: { action: 'clear_manual_stage', id: proposal.id },
      });
      if (error) throw error;
      onProposalUpdated?.();
    } catch (err) {
      alert(await fnErrorMessage(err, 'Failed to clear pin'));
    }
  }

  const isSystemLead = proposal.is_system_lead === true;

  return (
    <div
      className={`ops-board-card${isSystemLead ? ' ops-board-card--system' : ''}`}
      onClick={() => onView(proposal)}
      title={isSystemLead ? 'System lead, auto-attributed to POS Leads Agency from a paid-traffic landing-page submission.' : undefined}
    >
      <div className="ops-board-card-header">
        <span className="ops-board-card-customer" title={propertyName}>
          {propertyName}
          {isSystemLead && (
            <span
              style={{ display: 'inline-block', fontSize: 'var(--text-2xs)', fontWeight: 700, background: 'var(--color-error-bg)', color: 'var(--color-error-text)', padding: '1px 5px', borderRadius: 'var(--radius-sm)', marginLeft: '0.4rem', verticalAlign: 'middle', letterSpacing: '0.02em' }}
            >
              SYSTEM
            </span>
          )}
        </span>
        {ambassadorInitials && (
          <span
            title={ambassadorName}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: POS_GOLD,
              color: '#ffffff',
              fontSize: 'var(--text-xs)',
              fontWeight: 700,
              letterSpacing: '0.02em',
              flexShrink: 0,
            }}
          >
            {ambassadorInitials}
          </span>
        )}
      </div>

      <div className="ops-board-card-meta flex items-center justify-between gap-sm">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap', minWidth: 0 }}>
          {refCode && <code className="text-xs text-muted">{refCode}</code>}
          <ProposalTemperaturePill proposal={proposal} />
          {manualStage && (
            <span
              onClick={handleClearPin}
              title={`Pinned to ${manualStage} — click to unpin`}
              style={{
                fontSize: 'var(--text-xs)',
                padding: '0.125rem 0.375rem',
                background: 'var(--color-warning-bg)',
                color: 'var(--color-warning-text)',
                borderRadius: 'var(--radius-sm)',
                cursor: supabase ? 'pointer' : 'default',
                border: '1px solid #fbbf24',
              }}
            >
              📌 Pinned
            </span>
          )}
        </div>
        <span style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {formatCurrency(annualTotal)}/yr
        </span>
      </div>

      <div className="ops-board-card-meta mt-xs text-xs">
        <ActivityLine proposal={proposal} columnId={columnId} formatDate={formatDate} />
      </div>

      {isReworkNeeded && (
        <div
          style={{
            marginTop: '0.375rem',
            padding: '0.25rem 0.5rem',
            background: 'var(--color-error-bg)',
            color: 'var(--color-error)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
          }}
        >
          ⚠ Rework needed{proposal.rejection_reason ? `: ${proposal.rejection_reason}` : ''}
        </div>
      )}

      {latestEvent && <LatestEventPreview evt={latestEvent} />}

      {actions && (
        <div
          className="ops-board-card-actions flex gap-sm flex-wrap items-center mt-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {actions(proposal)}
        </div>
      )}
    </div>
  );
}

/**
 * Follow-up temperature pill. Uses the canonical status-pill temperature
 * classes, the same ones the property renewals board uses, so the two
 * screens read identically. Nothing renders once a proposal is settled.
 */
function ProposalTemperaturePill({ proposal }: { proposal: Row }) {
  const temp = computeProposalTemperature(proposal);
  if (!temp) return null;
  const age = proposalAgeDays(proposal);
  const cfg = PROPOSAL_TEMPERATURE_CONFIG[temp];
  return (
    <span
      className={`status-pill status-pill--temp-${temp}`}
      title={cfg.action}
    >
      {age !== null ? `${age}d` : cfg.label}
    </span>
  );
}

/**
 * Two-letter initials from a name. "Gary Harrod" → "GH", "Barry" → "B".
 * Empty / nullish input returns ''.
 */
function toInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '';
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  if (!last) return first.charAt(0).toUpperCase();
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

interface ActivityLineProps {
  proposal: Row;
  columnId: string;
  formatDate: (dateStr: string) => string;
}

function ActivityLine({ proposal, columnId, formatDate }: ActivityLineProps) {
  const now = Date.now();
  const daysSince = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    return Math.floor((now - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  const sentAgo = daysSince(proposal.sent_at as string | null);
  const viewedAgo = daysSince(proposal.viewed_at as string | null);
  const acceptedAgo = daysSince(proposal.accepted_at as string | null);
  const expiresIn = proposal.expires_at
    ? Math.ceil((new Date(proposal.expires_at as string).getTime() - now) / (1000 * 60 * 60 * 24))
    : null;
  const viewCount = Number(proposal.view_count) || 0;

  // Per-column primary activity line
  if (columnId === 'drafting') {
    const created = formatDate(proposal.created_at as string);
    return <span>Created {created}</span>;
  }
  if (columnId === 'awaiting_review') {
    const submittedAgo = daysSince(proposal.submitted_for_approval_at as string | null);
    return (
      <span style={{ color: submittedAgo !== null && submittedAgo >= 2 ? 'var(--color-error)' : undefined }}>
        Submitted {submittedAgo !== null ? `${submittedAgo}d ago` : 'recently'}
      </span>
    );
  }
  if (columnId === 'sent') {
    return <span>Sent {sentAgo !== null ? `${sentAgo}d ago` : 'recently'} · not opened</span>;
  }
  if (columnId === 'engaged') {
    return (
      <span>
        Viewed {viewedAgo !== null ? `${viewedAgo}d ago` : 'recently'}
        {viewCount > 1 ? ` · ${viewCount} views` : ''}
        {expiresIn !== null && expiresIn > 0 && expiresIn <= 7 && (
          <span style={{ color: 'var(--color-warning)', marginLeft: '0.5rem' }}>· expires in {expiresIn}d</span>
        )}
      </span>
    );
  }
  if (columnId === 'stalled') {
    return (
      <span style={{ color: 'var(--color-error)' }}>
        Last viewed {viewedAgo !== null ? `${viewedAgo}d ago` : 'a while ago'}
        {viewCount > 1 ? ` · ${viewCount} views` : ''}
      </span>
    );
  }
  if (columnId === 'won') {
    return (
      <span style={{ color: 'var(--color-success)' }}>
        Accepted {acceptedAgo !== null ? `${acceptedAgo}d ago` : 'recently'}
      </span>
    );
  }
  if (columnId === 'lost') {
    if (proposal.lost_reason) {
      return <span>Lost: {String(proposal.lost_reason)}</span>;
    }
    if (proposal.rejection_reason) {
      return <span>Rejected: {String(proposal.rejection_reason)}</span>;
    }
    return <span>Closed</span>;
  }
  return <span>{formatDate(proposal.created_at as string)}</span>;
}

// =============================================================================
// One-line preview of the latest ambassador-action event (manual_activity / sent_via_*
// / submitted_for_approval / approved_by_admin / rejected_by_admin). Customer-side
// activity doesn't surface here — that's already implied by the column the card is in.
// =============================================================================

interface LatestEventPreviewProps {
  evt: Row;
}

function LatestEventPreview({ evt }: LatestEventPreviewProps) {
  const data = (evt.event_data || {}) as Record<string, unknown>;
  const users = evt.users as { first_name?: string; last_name?: string } | null;
  const userName = users
    ? `${users.first_name || ''} ${users.last_name || ''}`.trim().split(' ')[0] || 'User'
    : 'User';

  const ts = new Date(evt.created_at as string);
  const daysSince = Math.floor((Date.now() - ts.getTime()) / (1000 * 60 * 60 * 24));
  const ago = daysSince === 0 ? 'today' : daysSince === 1 ? 'yesterday' : `${daysSince}d ago`;

  let icon = '·';
  let label = '';
  let body = '';

  switch (evt.event_type as string) {
    case 'manual_activity': {
      const t = (data.activity_type as string) || 'note';
      const icons: Record<string, string> = { note: '📝', call: '📞', meeting: '🤝', other: '🔹' };
      icon = icons[t] || '📝';
      label = `${userName}`;
      body = (data.body as string) || '';
      break;
    }
    case 'sent_via_email':    icon = '📧'; label = `${userName} sent via email`;    break;
    case 'sent_via_whatsapp': icon = '💬'; label = `${userName} sent via WhatsApp`; break;
    case 'sent_via_sms':      icon = '✉️'; label = `${userName} sent via SMS`;      break;
    case 'followup_sent':          icon = '📩'; label = `${userName} sent the follow-up email`;    break;
    case 'followup_whatsapp_sent': icon = '💬'; label = `${userName} sent the follow-up on WhatsApp`; break;
    case 'submitted_for_approval': icon = '📤'; label = `${userName} submitted for review`; break;
    case 'approved_by_admin': icon = '✅'; label = `${userName} approved`; break;
    case 'rejected_by_admin':
      icon = '❌';
      label = `${userName} rejected`;
      body = data.reason ? String(data.reason) : '';
      break;
    case 'manual_stage_change':
      icon = '📌';
      label = `${userName} moved card`;
      body = data.from && data.to ? `${data.from} → ${data.to}` : '';
      break;
    case 'marked_lost':
      icon = '❌';
      label = `${userName} marked as lost`;
      body = data.reason ? String(data.reason) : '';
      break;
    case 'marked_accepted_admin':
      icon = '✍️';
      label = `${userName} recorded customer acceptance`;
      body = data.note ? String(data.note) : '';
      break;
    case 're_engaged':
      icon = '↩️';
      label = `${userName} re-engaged proposal`;
      break;
    case 'recalled_to_drafting':
      icon = '↪️';
      label = `${userName} recalled to drafting`;
      body = data.reason ? String(data.reason) : '';
      break;
    default:
      return null;
  }

  const displayBody = body.length > 80 ? body.substring(0, 77) + '…' : body;

  return (
    <div
      title={body || label}
      style={{
        marginTop: '0.5rem',
        padding: '0.375rem 0.5rem',
        background: 'var(--color-bg)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--text-sm)',
        lineHeight: 1.35,
        color: 'var(--color-text-light)',
      }}
    >
      <span style={{ marginRight: '0.375rem' }}>{icon}</span>
      <strong style={{ color: 'var(--color-text)', fontWeight: 600 }}>{label}</strong>
      {displayBody && <span>: {displayBody}</span>}
      <span style={{ marginLeft: '0.375rem', opacity: 0.7 }}>· {ago}</span>
    </div>
  );
}
